import {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useRef,
} from 'react';
import isEqual from 'react-fast-compare';

import {useI18n} from '@yourcoach/i18n';
import {alert} from '@yourcoach/shared/components/alert';
import {logger} from '@yourcoach/shared/utils/logger';

import type {ProgramT} from '.';
import type {Ref as CocoachingManagerRef} from './CocoachingManager';
import type {TabProps, TabRef} from './useTabs';

import CocoachingManager from './CocoachingManager';
import Summary from './Summary';

import styles from './Complete.module.css';

const CRUProgramComplete = forwardRef<TabRef, TabProps>(
  (
    {cloneProgram, isOfferedProgram, program, shouldCloneProgram, userIsOwner},
    ref,
  ) => {
    const {t} = useI18n();

    useImperativeHandle(ref, () => ({
      getData,
    }));

    const showIsntProviderCoachesAlert = useCallback(
      (coaches: ProgramT['expanded_coaches']) =>
        new Promise<void>((resolve, reject) => {
          alert.show({
            buttons: [
              {
                label: t('shared:remove'),
                onClick: () => {
                  resolve();
                },
                scheme: 'primary',
              },
              {
                label: t('shared:cancel'),
                onClick: () => {
                  reject();
                },
              },
            ],
            message: t(
              'shared:CRUProgramComplete.isnt_provider_coaches_popup.description',
              {
                coaches: coaches.map(coach => coach.name).join(', '),
              },
            ),
            title: t(
              'shared:CRUProgramComplete.isnt_provider_coaches_popup.header',
            ),
          });
        }),
      [t],
    );

    const getData = useCallback(
      async (direction?: 'back' | 'next') => {
        if (!cocoachingManager.current) {
          return {};
        }

        const coachesData = cocoachingManager.current.getData();

        let {coaches} = coachesData;

        const {expandedCoaches, isntProviderCoaches} = coachesData;

        if (direction === 'next' && isntProviderCoaches.length) {
          try {
            await showIsntProviderCoachesAlert(isntProviderCoaches);

            cocoachingManager.current.removeIsntProviderCoaches();

            const updatedCoachesData = cocoachingManager.current.getData();

            coaches = updatedCoachesData.coaches;

            return;
          } catch (error) {
            logger.error(error);

            return;
          }
        }

        return {
          coach_ids: Object.keys(coaches),
          coaches,
          expanded_coaches: expandedCoaches,
        };
      },
      [showIsntProviderCoachesAlert],
    );

    const cocoachingManager = useRef<CocoachingManagerRef>(null);

    return (
      <div className={styles.container}>
        {program ? (
          <>
            <Summary program={program} />
            {!isOfferedProgram ? (
              <CocoachingManager
                className={styles.cocoachingManager}
                cloneProgram={cloneProgram}
                program={program}
                ref={cocoachingManager}
                shouldCloneProgram={shouldCloneProgram}
                userIsOwner={userIsOwner}
              />
            ) : null}
          </>
        ) : null}
      </div>
    );
  },
);

export default memo(CRUProgramComplete, isEqual);
